import { useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  exportJobs,
  importJobs,
  createBackup,
  listBackups,
  restoreBackup,
  deleteBackup,
  type ManagedJob,
} from '../../api/cron';
import { useSnackbar } from '../../context/SnackbarContext';
import { useConfirm } from '../../context/ConfirmDialogContext';

// ── Export ──────────────────────────────────────────────────────────────────

function ExportSection({ jobCount }: { jobCount: number }) {
  const showSnackbar = useSnackbar();
  const [loading, setLoading] = useState(false);

  async function handleExport() {
    setLoading(true);
    try {
      const blob = await exportJobs();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cronjobs-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Export failed', 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {jobCount} managed job{jobCount !== 1 ? 's' : ''} will be exported.
      </Typography>
      <Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <DownloadIcon />}
          disabled={loading || jobCount === 0}
          onClick={() => void handleExport()}
        >
          Download jobs as JSON
        </Button>
      </Box>
    </Stack>
  );
}

// ── Import ──────────────────────────────────────────────────────────────────

function ImportSection() {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingJobs, setPendingJobs] = useState<ManagedJob[] | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  const mutation = useMutation({
    mutationFn: () => importJobs(pendingJobs!, mode),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['cron', 'jobs'] });
      setResult(data);
      setPendingJobs(null);
    },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string) as { jobs?: ManagedJob[] };
        if (!Array.isArray(data.jobs)) throw new Error('Invalid format');
        setPendingJobs(data.jobs);
        setResult(null);
      } catch {
        showSnackbar('Invalid JSON file', 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}>
          Choose file (.json)
        </Button>
        <input ref={fileRef} type="file" accept=".json" hidden onChange={handleFile} />
        {pendingJobs && (
          <Typography variant="body2" color="text.secondary">
            {pendingJobs.length} job{pendingJobs.length !== 1 ? 's' : ''} found in file
          </Typography>
        )}
      </Stack>

      {pendingJobs && (
        <>
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as 'merge' | 'replace')}>
            <FormControlLabel value="merge" control={<Radio size="small" />} label="Merge (safe — skip duplicates)" />
            <FormControlLabel value="replace" control={<Radio size="small" />} label={<span style={{ color: '#f44336' }}>Replace (destructive — clears existing jobs)</span>} />
          </RadioGroup>
          {mode === 'replace' && (
            <Alert severity="warning" sx={{ py: 0 }}>All current managed jobs will be deleted before import.</Alert>
          )}
          <Box>
            <Button
              variant="contained"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Import
            </Button>
          </Box>
        </>
      )}

      {result && (
        <Alert severity="success" sx={{ py: 0 }}>
          Imported {result.imported}, skipped {result.skipped}
        </Alert>
      )}
    </Stack>
  );
}

// ── Backups ─────────────────────────────────────────────────────────────────

function BackupsSection() {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();
  const confirm = useConfirm();
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cron', 'backups'],
    queryFn: listBackups,
  });

  const createMut = useMutation({
    mutationFn: createBackup,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cron', 'backups'] }); showSnackbar('Backup created', 'success'); },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  const restoreMut = useMutation({
    mutationFn: (filename: string) => restoreBackup(filename),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['cron'] });
      showSnackbar(`Restored: ${r.imported} imported, ${r.skipped} skipped`, 'success');
      setRestoreTarget(null);
    },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: (filename: string) => deleteBackup(filename),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cron', 'backups'] }); },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  async function handleDelete(filename: string) {
    const ok = await confirm(`Delete backup "${filename}"?`, { title: 'Delete backup', confirmLabel: 'Delete', danger: true });
    if (ok) deleteMut.mutate(filename);
  }

  const fmtTs = (ts: string) => {
    try { return new Date(ts).toLocaleString('de-DE'); } catch { return ts; }
  };

  return (
    <Stack spacing={1.5}>
      <Box>
        <Button
          variant="outlined"
          startIcon={createMut.isPending ? <CircularProgress size={14} /> : <AddCircleIcon />}
          disabled={createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          Create backup now
        </Button>
      </Box>

      {isLoading && <CircularProgress size={20} />}

      {data && data.backups.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Jobs</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.backups.map((b) => (
              <TableRow key={b.filename} hover>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">{fmtTs(b.createdAt)}</Typography>
                </TableCell>
                <TableCell><Chip label={b.jobCount} size="small" /></TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Restore">
                      <IconButton size="small" onClick={() => setRestoreTarget(b.filename)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete backup">
                      <IconButton size="small" color="error" onClick={() => void handleDelete(b.filename)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {data && data.backups.length === 0 && (
        <Typography variant="body2" color="text.secondary">No backups yet.</Typography>
      )}

      {/* Restore confirm dialog */}
      <Dialog open={restoreTarget !== null} onClose={() => setRestoreTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Restore backup</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This will replace <strong>all</strong> managed cron jobs with the contents of this backup. Continue?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={restoreMut.isPending}
            onClick={() => restoreTarget && restoreMut.mutate(restoreTarget)}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────────

export function ImportExportPanel({ managedJobCount }: { managedJobCount: number }) {
  return (
    <Box mt={3}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Import / Export / Backups</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Export</Typography>
              <ExportSection jobCount={managedJobCount} />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Import</Typography>
              <ImportSection />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Backups</Typography>
              <BackupsSection />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
